import { PUBLIC_DIRECTUS_BACKEND } from '$env/static/public';
import { GraphQLClient } from 'graphql-request';
import { getPreviewHeader } from './pagebuilder';

/**
 * Function for making requests to our directus backend. Automatically injects translations using injectTranslations.
 * @param url         The url of the request without the base url
 * @param svelteFetch The fetch function svelte provides in the page/layout load function (better optimized than the standard fetch)
 * 										If not available, set it to null and the function will fallback to the standard fetch method.
 * @param lang				The language that should be returned
 * @returns 					The data from the directus backend
 */

export const getDirectus = async (url: string, svelteFetch: any, lang: string = 'en', previewToken?: string) => {
	const prevHeader = getPreviewHeader(previewToken)

	let response = null
	if (svelteFetch) {
		response = await svelteFetch(PUBLIC_DIRECTUS_BACKEND + url, { headers: prevHeader });
	} else {
		response = await fetch(PUBLIC_DIRECTUS_BACKEND + url, { headers: prevHeader });
	}
	const data = await response.json();

	return injectTranslations(data, lang);
};

/**
 * Function for making requests to our directus backend. Returns raw data without injecting translations
 * @param url         The url of the request without the base url
 * @param svelteFetch The fetch function svelte provides in the page/layout load function (better optimized than the standard fetch)
 * 										If not available, set it to null and the function will fallback to the standard fetch method.
 * @returns 					The data from the directus backend
 */

export const getDirectusRaw = async (url: string, svelteFetch: any, previewToken?: string) => {
	const prevHeader = getPreviewHeader(previewToken)

	let response = null
	if (svelteFetch) {
		response = await svelteFetch(PUBLIC_DIRECTUS_BACKEND + url, { headers: prevHeader });
	} else {
		response = await fetch(PUBLIC_DIRECTUS_BACKEND + url, { headers: prevHeader });
	}

	const data = await response.json();

	return data;
};

/**
 * Injects translations into its parent for easier access.
 *
 * @param data 		The directus data that should be transformed
 * @param lang		The language that should be returned
 * @returns 			The data with the translations injected into their parents
 */
export const injectTranslations = (data: any, lang: string) => {
	const translationFields = ['translations', 'translations_text', 'translations_richtext'];

	const handle = (item: any): any => {
		if (item === null || item === undefined) {
			return null;
		} else if (typeof item === 'object') {
			for (const i in item) {
				item[i] = handle(item[i]);
			}

			for (const tf of translationFields) {
				if (item[tf]) {
					let trans;

					if (item[tf].length === 0) {
						return item;
					}

					// Try fetching current locale
					for (const translation of item[tf]) {
						if (
							translation?.languages_code === lang ||
							translation?.languages_code?.code === lang
						) {
							trans = translation;
						}
					}

					// If current locale not available, try fetching default locale
					if (!trans) {
						for (const translation of item[tf]) {
							if (
								translation?.languages_code === 'en' ||
								translation?.languages_code?.code === 'en'
							) {
								trans = translation;
							}
						}
					}

					// If no matching translation could be found until here, just select to first
					if (!trans) {
						trans = item[tf][0];
					}

					// remove unused / overlapping values
					delete trans.id;
					delete trans.languages_code;
					delete trans.languages_id;

					item = { ...item, ...trans }; // inject first transaltion item in current item
					delete item[tf]; // remove translations property from item
				}
			}

			return item;
		} else if (Array.isArray(item)) {
			return item.map((i) => handle(i));
		} else {
			return item;
		}
	};

	return handle(data);
};

export const gqlClient = new GraphQLClient(PUBLIC_DIRECTUS_BACKEND + 'graphql');
